import { ArrowLeftOutlined, DeleteOutlined } from '@ant-design/icons'
import { usePermissions } from '@refinedev/core'
import { Breadcrumb, Button, Card, Descriptions, Space, Spin } from 'antd'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { hasPermission } from '@/auth/capabilities'
import { FormattedDate } from '@/components/common/FormattedDate'
import { isSystemRole } from '@/constants/roles'

import { RoleDeleteModal } from './components/RoleDeleteModal'
import { RoleEditor } from './components/RoleEditor'
import { roleLoadError } from './errorState'
import { useRoleDetail } from './hooks/useRoleDetail'
import { decodeRoleParam } from './routeParam'

export default function RoleShowPage() {
  const { id } = useParams<{ id: string }>()
  const roleName = decodeRoleParam(id)
  const navigate = useNavigate()
  const [deleteOpen, setDeleteOpen] = useState(false)

  const { role, isLoading, error, refetch } = useRoleDetail(roleName)
  const { data: permissions = [] } = usePermissions<string[]>({})
  const canWrite = hasPermission(permissions, 'admin:roles:write')

  if (isLoading) {
    return <Spin style={{ display: 'block', marginTop: 48 }} />
  }

  if (error) {
    const status = (error as { statusCode?: number }).statusCode
    const errorState = roleLoadError(status)
    return (
      <div>
        <Breadcrumb
          style={{ marginBottom: 16 }}
          items={[
            { title: <Link to="/roles">Roles</Link> },
            { title: errorState.title },
          ]}
        />
        <Card>
          <Space direction="vertical">
            <span>{errorState.message}</span>
            {errorState.retry && (
              <Button onClick={() => refetch()}>Retry</Button>
            )}
          </Space>
        </Card>
      </div>
    )
  }

  if (!role || !roleName) return null

  return (
    <div>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <Link to="/roles">Roles</Link> },
          { title: role.name },
        ]}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <h2 style={{ margin: 0 }}>{role.name}</h2>
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/roles')}
          >
            Back
          </Button>
          {canWrite && !isSystemRole(role.name) && (
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => setDeleteOpen(true)}
            >
              Delete
            </Button>
          )}
        </Space>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Descriptions column={1}>
          <Descriptions.Item label="Created">
            <FormattedDate date={role.createdAt} />
          </Descriptions.Item>
          <Descriptions.Item label="Updated">
            <FormattedDate date={role.updatedAt} />
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <RoleEditor
        key={role.name}
        role={role}
        refetch={refetch}
        canWrite={canWrite}
        callerPermissions={permissions}
      />
      <RoleDeleteModal
        roleName={deleteOpen ? role.name : undefined}
        onCancel={() => setDeleteOpen(false)}
        onDeleted={() => void navigate('/roles')}
      />
    </div>
  )
}
